import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock, Copy, ExternalLink, KeyRound, Loader2, Mail, MoreHorizontal, RefreshCw, Shield, UserMinus, UserPlus, X } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { updateUserRole, removeMember } from '../../../services/organizationService';
import { listCargos, assignUserToCargo } from '../../../services/capacidadeService';
import { getDepartments, getRoles } from '../../../services/organizationStructureService';
import type { OrgDepartment, OrgRole } from '../../../services/organizationStructureService';
import { InvitationService } from '../../../services/invitationService';
import { BulkInviteTab } from '../components/BulkInviteTab';

// ─── CPF helpers ─────────────────────────────────────────────────────────────

/** Format raw digits into 000.000.000-00 mask */
function maskCpf(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Synthetic internal email derived from CPF digits */
function cpfToInternalEmail(cpf: string): string {
  const d = cpf.replace(/\D/g, '');
  return `cpf_${d}@internal-workflow.com`;
}

/** Provisional password for industrial first-login */
/** Formats a synthetic CPF email into a human-readable identifier */
function formatEmailDisplay(email: string | null): { isCpf: boolean; display: string } {
  if (!email) return { isCpf: false, display: '-' };
  const match = email.match(/^cpf_(\d{11})@internal-workflow\.com$/);
  if (match) {
    const d = match[1];
    return { isCpf: true, display: `\u{1F464} CPF: ${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}` };
  }
  return { isCpf: false, display: email };
}

function cpfToProvisionalPassword(cpf: string): string {
  const d = cpf.replace(/\D/g, '');
  return `BrFlow@${d.slice(0, 4)}`;
}

interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
  role: string | null;
  organization_id: string | null;
  updated_at: string | null;
  department_id: string | null;
  role_id: string | null;
  /** true = pending invitation (not yet accepted) */
  isPending?: boolean;
  invitationId?: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner:    'Owner (Proprietário)',
  admin:    'Administrador',
  operator: 'Operador',
  viewer:   'Visualizador',
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner:    'Owner (Proprietário) — Acesso total, segurança e faturamento',
  admin:    'Administrador — Gestão de pessoas, processos e relatórios',
  operator: 'Operador — Execução de tarefas e movimentação de fluxos',
  viewer:   'Visualizador — Somente leitura (auditorias e dashboards)',
};

/** Mapa de fallback para exibição de roles legados armazenados no banco */
const ROLE_LABELS_LEGACY: Record<string, string> = {
  admin: 'Administrador',
  manager: 'Administrador',
  gestor: 'Administrador',
  consultor_master: 'Owner (Proprietário)',
  user: 'Operador',
  usuario: 'Operador',
  viewer: 'Visualizador',
  visualizador: 'Visualizador',
};

const roleBadge = (role: string | null) => {
  const r = String(role ?? '').toLowerCase();
  if (r === 'owner' || r === 'consultor_master') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
  if (r === 'admin' || r === 'manager' || r === 'gestor') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (r === 'operator' || r === 'operador' || r === 'user' || r === 'usuario') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (r === 'viewer' || r === 'visualizador') return 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400';
  return 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400';
};

/** Retorna o label de exibição para qualquer valor de role (novo ou legado) */
const resolveRoleLabel = (role: string | null | undefined): string => {
  const r = String(role ?? '').toLowerCase();
  return ROLE_LABELS[r] ?? ROLE_LABELS_LEGACY[r] ?? (role ? role.toUpperCase() : '—');
};

interface AdminUsersPageProps {
  externalSearch?: string;
  externalRoleFilter?: string;
  externalShowInviteModal?: boolean;
  onCloseInviteModal?: () => void;
}

export const AdminUsersPage: React.FC<AdminUsersPageProps> = ({
  externalSearch,
  externalRoleFilter,
  externalShowInviteModal,
  onCloseInviteModal,
}) => {
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const organizationId = workspace?.organizationId || null;
  const search = externalSearch ?? '';
  const roleFilter = externalRoleFilter ?? 'all';
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [roleModalUser, setRoleModalUser] = useState<UserRow | null>(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmRemoveUser, setConfirmRemoveUser] = useState<UserRow | null>(null);
  const showInviteModal = externalShowInviteModal ?? false;
  const [inviteTab, setInviteTab] = useState<'individual' | 'bulk'>('individual');
  const [inviteAuthMode, setInviteAuthMode] = useState<'email' | 'cpf'>('email');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteCpf, setInviteCpf] = useState('');
  const [cpfCopied, setCpfCopied] = useState(false);
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviteDeptId, setInviteDeptId] = useState('');
  const [inviteOrgRoleId, setInviteOrgRoleId] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteErrorDetail, setInviteErrorDetail] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const handleChangeRole = async () => {
    if (!organizationId || !roleModalUser || !selectedRole) return;
    setIsUpdating(true);
    try {
      await updateUserRole(organizationId, roleModalUser.id, selectedRole);
      await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
    } catch (err) {
      console.error('Erro ao alterar papel:', err);
    } finally {
      setIsUpdating(false);
      setRoleModalUser(null);
    }
  };

  const handleInviteUser = async () => {
    if (!organizationId) return;
    setIsInviting(true);
    setInviteError(null);
    setInviteErrorDetail(null);

    try {
      let emailToUse: string;

      if (inviteAuthMode === 'cpf') {
        const digits = inviteCpf.replace(/\D/g, '');
        if (!inviteFullName.trim()) throw new Error('Nome completo é obrigatório.');
        if (digits.length !== 11) throw new Error('CPF/Matrícula deve ter 11 dígitos.');
        emailToUse = cpfToInternalEmail(digits);
      } else {
        if (!inviteEmail.trim()) throw new Error('E-mail é obrigatório.');
        emailToUse = inviteEmail.trim();
      }

      await InvitationService.create({
        organization_id: organizationId,
        email: emailToUse,
        role: inviteRole,
        department_id: inviteDeptId || null,
        org_role_id: inviteOrgRoleId || null,
        organizationName: workspace?.organizationName ?? '',
      });

      await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
      onCloseInviteModal?.();
      // Reset form
      setInviteEmail('');
      setInviteFullName('');
      setInviteCpf('');
      setInviteRole('operator');
      setInviteDeptId('');
      setInviteOrgRoleId('');
      setInviteTab('individual');
      setInviteAuthMode('email');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao convidar usuário.';
      setInviteError(msg);
      // Capture raw Supabase error detail for diagnostics
      if (err && typeof err === 'object' && 'details' in err) {
        setInviteErrorDetail(String((err as any).details ?? ''));
      } else if (err && typeof err === 'object' && 'hint' in err) {
        setInviteErrorDetail(String((err as any).hint ?? ''));
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!organizationId || !confirmRemoveUser) return;
    setIsUpdating(true);
    try {
      await removeMember(organizationId, confirmRemoveUser.id);
      await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
    } finally {
      setIsUpdating(false);
      setConfirmRemoveUser(null);
    }
  };

  const cargosQuery = useQuery({
    queryKey: ['admin-cargos', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => listCargos(organizationId!),
  });

  const departmentsQuery = useQuery<OrgDepartment[]>({
    queryKey: ['admin-departments', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => getDepartments(organizationId!),
    staleTime: 60_000,
  });

  const orgRolesQuery = useQuery<OrgRole[]>({
    queryKey: ['admin-org-roles', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => getRoles(organizationId!),
    staleTime: 60_000,
  });

  // Roles filtered by selected department in invite modal
  const filteredInviteRoles = (orgRolesQuery.data ?? []).filter(
    (r) => !inviteDeptId || r.departmentId === inviteDeptId,
  );

  const cargoAssignmentsQuery = useQuery<Map<string, string>>({
    queryKey: ['admin-cargo-assignments', organizationId],
    enabled: Boolean(organizationId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_cargo_assignment')
        .select('user_id, cargo_id')
        .eq('organization_id', organizationId!);
      if (error) throw error;
      const map = new Map<string, string>();
      (data ?? []).forEach((row: { user_id: string; cargo_id: string }) => map.set(row.user_id, row.cargo_id));
      return map;
    },
  });

  const assignCargoMut = useMutation({
    mutationFn: ({ userId, cargoId }: { userId: string; cargoId: string }) =>
      assignUserToCargo(organizationId!, userId, cargoId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-cargo-assignments', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['org-wip-max', organizationId] });
    },
  });

  const usersQuery = useQuery({
    queryKey: ['admin-users-table', organizationId],
    enabled: Boolean(organizationId),
    queryFn: async (): Promise<UserRow[]> => {
      if (!organizationId) return [];

      // ── Active members ───────────────────────────────────────────
      const memberships = await supabase
        .from('user_organizations')
        .select('user_id, role, organization_id, department_id, role_id, created_at')
        .eq('organization_id', organizationId)
        .limit(200);

      if (memberships.error) throw memberships.error;

      const membershipRows = memberships.data || [];
      let activeRows: UserRow[] = [];

      if (membershipRows.length) {
        const userIds = membershipRows.map((row: any) => row.user_id).filter(Boolean);

        const profilesByUserId = await supabase
          .from('user_profiles')
          .select('user_id, name, email, updated_at')
          .in('user_id', userIds)
          .limit(200);

        let profiles = profilesByUserId.data || [];

        if (profilesByUserId.error) {
          const fallbackProfiles = await supabase
            .from('user_profiles')
            .select('id, name, email, updated_at')
            .in('id', userIds)
            .limit(200);

          if (fallbackProfiles.error) throw fallbackProfiles.error;

          profiles = (fallbackProfiles.data || []).map((row: any) => ({
            user_id: row.id,
            name: row.name,
            email: row.email,
            updated_at: row.updated_at,
          }));
        }

        const profileMap = new Map<string, any>();
        profiles.forEach((profile: any) => {
          if (profile?.user_id) profileMap.set(String(profile.user_id), profile);
        });

        activeRows = membershipRows.map((membership: any) => {
          const profile = profileMap.get(String(membership.user_id));
          return {
            id: String(membership.user_id),
            name: profile?.name ?? null,
            email: profile?.email ?? null,
            role: membership.role ?? null,
            organization_id: membership.organization_id ?? organizationId,
            updated_at: profile?.updated_at ?? membership.created_at ?? null,
            department_id: membership.department_id ?? null,
            role_id: membership.role_id ?? null,
            isPending: false,
          };
        });
      }

      // ── Pending invitations (not yet accepted) ───────────────────
      const { data: pendingInvites, error: invErr } = await supabase
        .from('invitations')
        .select('id, email, role, created_at')
        .eq('organization_id', organizationId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(100);

      if (invErr) {
        // Non-fatal: table may not exist yet before migration
        console.warn('[AdminUsersPage] Could not fetch pending invitations:', invErr.message);
      }

      const activeEmails = new Set(activeRows.map((r) => r.email?.toLowerCase()).filter(Boolean));

      const pendingRows: UserRow[] = (pendingInvites ?? [])
        .filter((inv: any) => !activeEmails.has(inv.email?.toLowerCase()))
        .map((inv: any) => ({
          id: `invite_${inv.id}`,
          name: null,
          email: inv.email,
          role: inv.role,
          organization_id: organizationId,
          updated_at: inv.created_at,
          department_id: null,
          role_id: null,
          isPending: true,
          invitationId: inv.id,
        }));

      return [...activeRows, ...pendingRows];
    },
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
        <div className="enterprise-page h-full flex flex-col overflow-hidden">

          {usersQuery.isPending && <ModuleLoadingState label="Carregando usuarios" />}

          {usersQuery.isError && (
            <ErrorState
              message="Nao foi possivel carregar a listagem de usuarios da organizacao ativa."
              onRetry={usersQuery.refetch}
            />
          )}

          {!usersQuery.isPending && !usersQuery.isError && !usersQuery.data?.length && (
            <EmptyState
              title="Nenhum usuario encontrado"
              description="Nao ha usuarios visiveis para a organizacao ativa no momento."
              ctaLabel="Tentar novamente"
              onCta={() => usersQuery.refetch()}
            />
          )}

          {!usersQuery.isPending && !usersQuery.isError && Boolean(usersQuery.data?.length) && (() => {
            const filtered = (usersQuery.data ?? []).filter((row) => {
              const matchSearch = !search ||
                (row.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
                (row.email ?? '').toLowerCase().includes(search.toLowerCase());
              const matchRole = roleFilter === 'all' || row.role === roleFilter;
              return matchSearch && matchRole;
            });
            return (
              <>
                {/* Table — edge-to-edge */}
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full table-fixed text-left text-sm">
                    <colgroup>
                      <col className="w-[180px]" />
                      <col className="w-[210px]" />
                      <col className="w-[110px]" />
                      <col className="w-[160px]" />
                      <col />
                      <col className="w-[44px]" />
                    </colgroup>
                    <thead className="enterprise-table-header sticky top-0 z-10">
                      <tr>
                        <th className="enterprise-table-head-cell">Nome</th>
                        <th className="enterprise-table-head-cell">Email</th>
                        <th className="enterprise-table-head-cell" title="Define apenas acesso ao sistema, não posição na estrutura">Acesso</th>
                        <th className="enterprise-table-head-cell">Estrutura</th>
                        <th className="enterprise-table-head-cell">Cargo / Capacidade</th>
                        <th className="enterprise-table-head-cell" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row) => {
                        const isIncomplete = !row.department_id || !row.role_id;
                        return (
                        <tr key={row.id} className="enterprise-table-row">
                          <td className="enterprise-table-cell font-medium">
                            {row.name || (row.isPending ? <span className="italic enterprise-text-secondary text-[11px]">Aguardando aceite</span> : '—')}
                          </td>
                          <td className="enterprise-table-cell">
                            {(() => {
                              const { isCpf, display } = formatEmailDisplay(row.email);
                              return (
                                <span className={isCpf ? 'font-mono text-[11px] text-amber-700 dark:text-amber-400' : 'font-mono text-[12px]'}>
                                  {display}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="enterprise-table-cell">
                            <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${roleBadge(row.role)}`}>
                              {resolveRoleLabel(row.role)}
                            </span>
                          </td>
                          <td className="enterprise-table-cell">
                            {row.isPending ? (
                              <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                <Clock size={10} /> Pendente
                              </span>
                            ) : isIncomplete ? (
                              <div className="flex items-center gap-1.5">
                                <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                  <AlertTriangle size={10} /> Não configurado
                                </span>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/administracao/pessoas?tab=hierarquia`)}
                                  className="text-[10px] text-blue-500 hover:text-blue-600 hover:underline flex items-center gap-0.5"
                                >
                                  Completar estrutura <ExternalLink size={9} />
                                </button>
                              </div>
                            ) : (
                              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">Configurado</span>
                            )}
                          </td>
                          {/* Cargo / Capacidade */}
                          <td className="enterprise-table-cell">
                            {row.isPending ? (
                              <span className="text-[11px] enterprise-text-secondary italic">—</span>
                            ) : (() => {
                              const assignedCargoId = cargoAssignmentsQuery.data?.get(row.id) ?? null;
                              const cargos = cargosQuery.data ?? [];
                              return (
                                <div className="flex flex-col gap-0.5">
                                  <select
                                    className="enterprise-input text-[11px] h-[24px] min-w-[140px]"
                                    value={assignedCargoId ?? ''}
                                    onChange={e => {
                                      if (e.target.value) {
                                        assignCargoMut.mutate({ userId: row.id, cargoId: e.target.value });
                                      }
                                    }}
                                  >
                                    <option value="">— sem cargo —</option>
                                    {cargos.map(c => (
                                      <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                  </select>
                                  {!assignedCargoId && (
                                    <span className="flex items-center gap-1 text-[10px] text-amber-600">
                                      <AlertTriangle size={9} /> Defina um cargo para análise de capacidade
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="enterprise-table-cell text-right">
                            <button
                              type="button"
                              ref={openMenuId === row.id ? menuBtnRef : undefined}
                              className="p-1 rounded hover:bg-enterprise-panel enterprise-text-secondary transition-colors"
                              title="Ações"
                              onClick={(e) => {
                                if (openMenuId === row.id) {
                                  setOpenMenuId(null);
                                } else {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setMenuPos({ top: rect.bottom + 4, left: rect.right - 176 });
                                  setOpenMenuId(row.id);
                                }
                              }}
                            >
                              <MoreHorizontal size={14} />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>

      {/* Dropdown de ações (fixed, fora do overflow da tabela) */}
      {openMenuId && (() => {
        const row = (usersQuery.data ?? []).find((u) => u.id === openMenuId);
        if (!row) return null;

        if (row.isPending && row.invitationId) {
          return (
            <div ref={menuRef} className="fixed z-[90] w-52 rounded border enterprise-border bg-[var(--bg-surface)] shadow-lg py-1 text-sm" style={{ top: menuPos.top, left: menuPos.left }}>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 hover:bg-enterprise-panel flex items-center gap-2"
                onClick={async () => {
                  setOpenMenuId(null);
                  try {
                    await InvitationService.resend(row.invitationId!);
                    await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
                  } catch (e) {
                    console.error('Reenvio falhou', e);
                  }
                }}
              >
                <RefreshCw size={13} /> Reenviar E-mail de Convite
              </button>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 hover:bg-enterprise-panel flex items-center gap-2 text-red-500"
                onClick={async () => {
                  setOpenMenuId(null);
                  try {
                    await InvitationService.revoke(row.invitationId!);
                    await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
                  } catch (e) {
                    console.error('Cancelamento falhou', e);
                  }
                }}
              >
                <X size={13} /> Cancelar Convite
              </button>
            </div>
          );
        }

        return (
          <div ref={menuRef} className="fixed z-[90] w-44 rounded border enterprise-border bg-[var(--bg-surface)] shadow-lg py-1 text-sm" style={{ top: menuPos.top, left: menuPos.left }}>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-enterprise-panel flex items-center gap-2"
              onClick={() => { setOpenMenuId(null); setSelectedRole(row.role || ''); setRoleModalUser(row); }}
            >
              <Shield size={13} /> Alterar Acesso
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-enterprise-panel flex items-center gap-2"
              onClick={() => { setOpenMenuId(null); navigate(`/administracao/pessoas?tab=hierarquia`); }}
            >
              <ExternalLink size={13} /> Gerenciar estrutura
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-enterprise-panel flex items-center gap-2 text-red-500"
              onClick={() => { setOpenMenuId(null); setConfirmRemoveUser(row); }}
            >
              <UserMinus size={13} /> Remover da Org
            </button>
          </div>
        );
      })()}

      {/* Modal: Alterar Papel */}
      {roleModalUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !isUpdating && setRoleModalUser(null)}>
          <div className="bg-[var(--bg-surface)] rounded border enterprise-border shadow-xl w-96 p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-1">Alterar Acesso</h3>
            <p className="text-xs enterprise-text-secondary mb-4">
              Define apenas acesso ao sistema, não posição na estrutura.
              <br />{roleModalUser.name || roleModalUser.email}
            </p>
            <select
              className="enterprise-input w-full text-sm mb-4"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {Object.entries(ROLE_DESCRIPTIONS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button type="button" className="enterprise-button-secondary px-3 py-1.5 text-xs" onClick={() => setRoleModalUser(null)} disabled={isUpdating}>
                Cancelar
              </button>
              <button
                type="button"
                className="enterprise-btn-primary px-3 py-1.5 text-xs"
                onClick={handleChangeRole}
                disabled={isUpdating || selectedRole === roleModalUser.role}
              >
                {isUpdating ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Convidar Usuário */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !isInviting && onCloseInviteModal?.()}
        >
          <div
            className="bg-[var(--bg-surface)] rounded border enterprise-border shadow-xl w-[520px] p-5 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-2">
              <Mail size={15} />
              <h3 className="text-sm font-semibold">Convidar Usuário</h3>
            </div>

            {/* Tab bar: Individual / Em Massa */}
            <div className="flex gap-1 rounded border enterprise-border p-0.5 bg-[var(--bg-elevated)] w-fit">
              {(['individual', 'bulk'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`px-3 py-1 text-[11px] rounded font-medium transition-colors ${
                    inviteTab === tab
                      ? 'bg-[var(--bg-surface)] shadow-sm enterprise-text-primary'
                      : 'enterprise-text-secondary hover:enterprise-text-primary'
                  }`}
                  onClick={() => setInviteTab(tab)}
                >
                  {tab === 'individual' ? 'Individual' : 'Em Massa'}
                </button>
              ))}
            </div>

            {/* ── Individual tab ────────────────────────────────────── */}
            {inviteTab === 'individual' && (() => {
              const cpfDigits = inviteCpf.replace(/\D/g, '');
              const provisionalPwd = inviteAuthMode === 'cpf' && cpfDigits.length >= 4
                ? cpfToProvisionalPassword(cpfDigits)
                : '';
              const canSubmit = inviteAuthMode === 'email'
                ? inviteEmail.trim().length > 0
                : inviteFullName.trim().length > 0 && cpfDigits.length === 11;

              return (
                <>
                  {/* Auth mode toggle */}
                  <div className="flex items-center gap-2 -mt-1">
                    <span className="text-[11px] enterprise-text-secondary">Tipo de acesso:</span>
                    <div className="flex gap-0.5 rounded border enterprise-border p-0.5 bg-[var(--bg-elevated)]">
                      {(['email', 'cpf'] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          className={`flex items-center gap-1 px-2.5 py-1 text-[11px] rounded font-medium transition-colors ${
                            inviteAuthMode === mode
                              ? 'bg-[var(--bg-surface)] shadow-sm enterprise-text-primary'
                              : 'enterprise-text-secondary hover:enterprise-text-primary'
                          }`}
                          onClick={() => setInviteAuthMode(mode)}
                        >
                          {mode === 'email' ? <><Mail size={10} /> E-mail</> : <><KeyRound size={10} /> CPF / Matrícula</>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {inviteAuthMode === 'email' ? (
                    <>
                      <p className="text-xs enterprise-text-secondary -mt-2">
                        O usuário receberá um link por e-mail para ativar sua conta. Ele será vinculado automaticamente ao setor e cargo definidos abaixo.
                      </p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">E-mail do Usuário</label>
                          <input
                            type="email"
                            placeholder="usuario@empresa.com"
                            className="enterprise-input w-full text-sm"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Papel de Acesso</label>
                          <select className="enterprise-input w-full text-sm" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                            {Object.entries(ROLE_DESCRIPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Departamento / Setor</label>
                            <select className="enterprise-input w-full text-sm" value={inviteDeptId} onChange={(e) => { setInviteDeptId(e.target.value); setInviteOrgRoleId(''); }}>
                              <option value="">— nenhum —</option>
                              {(departmentsQuery.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Cargo</label>
                            <select className="enterprise-input w-full text-sm" value={inviteOrgRoleId} onChange={(e) => setInviteOrgRoleId(e.target.value)} disabled={filteredInviteRoles.length === 0}>
                              <option value="">— nenhum —</option>
                              {filteredInviteRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-xs enterprise-text-secondary -mt-2">
                        Cria um acesso industrial por CPF/Matrícula. Não é necessário e-mail real. Compartilhe a senha provisória diretamente com o operador.
                      </p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Nome Completo <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            placeholder="João da Silva"
                            className="enterprise-input w-full text-sm"
                            value={inviteFullName}
                            onChange={(e) => setInviteFullName(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">CPF / Matrícula <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            placeholder="000.000.000-00"
                            className="enterprise-input w-full text-sm font-mono"
                            value={inviteCpf}
                            onChange={(e) => setInviteCpf(maskCpf(e.target.value))}
                            maxLength={14}
                            inputMode="numeric"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Papel de Acesso</label>
                          <select className="enterprise-input w-full text-sm" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                            {Object.entries(ROLE_DESCRIPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Departamento / Setor</label>
                            <select className="enterprise-input w-full text-sm" value={inviteDeptId} onChange={(e) => { setInviteDeptId(e.target.value); setInviteOrgRoleId(''); }}>
                              <option value="">— nenhum —</option>
                              {(departmentsQuery.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">Cargo</label>
                            <select className="enterprise-input w-full text-sm" value={inviteOrgRoleId} onChange={(e) => setInviteOrgRoleId(e.target.value)} disabled={filteredInviteRoles.length === 0}>
                              <option value="">— nenhum —</option>
                              {filteredInviteRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          </div>
                        </div>
                        {/* Provisional password preview */}
                        {provisionalPwd && (
                          <div className="flex items-center justify-between rounded border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-900/20 px-3 py-2">
                            <div>
                              <p className="text-[10px] font-medium text-amber-700 dark:text-amber-400 mb-0.5">Senha Provisória (compartilhe com o operador)</p>
                              <code className="text-sm font-bold text-amber-800 dark:text-amber-300 tracking-wider">{provisionalPwd}</code>
                            </div>
                            <button
                              type="button"
                              title="Copiar senha"
                              className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-800/40 text-amber-600 transition-colors"
                              onClick={() => {
                                navigator.clipboard.writeText(provisionalPwd).then(() => {
                                  setCpfCopied(true);
                                  setTimeout(() => setCpfCopied(false), 2000);
                                });
                              }}
                            >
                              {cpfCopied ? <span className="text-[10px] text-emerald-600">Copiado!</span> : <Copy size={13} />}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Error block with diagnostic detail */}
                  {inviteError && (
                    <div className="rounded border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">{inviteError}</p>
                      {inviteErrorDetail && (
                        <p className="text-[11px] text-red-500 dark:text-red-400/80 mt-0.5 font-mono">{inviteErrorDetail}</p>
                      )}
                      {(inviteError.toLowerCase().includes('email') || inviteError.toLowerCase().includes('smtp')) && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-red-500 cursor-pointer hover:underline">▶ Diagnóstico SMTP</summary>
                          <ul className="mt-1 space-y-0.5 text-[11px] text-red-500 dark:text-red-400/80 list-disc list-inside">
                            <li>Supabase → Settings → Auth → SMTP: configure Host, Port, User, Password</li>
                            <li>Provedor recomendado: Resend (resend.com) ou SendGrid</li>
                            <li>Verifique o campo <code>Sender email</code> e confirme o domínio no provedor</li>
                            <li>O limite gratuito interno do Supabase é 3 e-mails/hora — SMTP externo remove o limite</li>
                            <li>Confirme que <code>Site URL</code> e <code>Redirect URLs</code> incluem a URL atual do ambiente</li>
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button type="button" className="enterprise-button-secondary px-3 py-1.5 text-xs" onClick={() => onCloseInviteModal?.()} disabled={isInviting}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="enterprise-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5"
                      onClick={handleInviteUser}
                      disabled={isInviting || !canSubmit}
                    >
                      {isInviting ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                      {isInviting ? 'Criando acesso...' : inviteAuthMode === 'cpf' ? 'Criar Acesso Industrial' : 'Enviar Convite'}
                    </button>
                  </div>
                </>
              );
            })()}

            {/* ── Em Massa tab ──────────────────────────────────────── */}
            {inviteTab === 'bulk' && organizationId && (
              <BulkInviteTab
                organizationId={organizationId}
                inviteRole={inviteRole}
                departments={departmentsQuery.data ?? []}
                orgRoles={orgRolesQuery.data ?? []}
                onComplete={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['admin-users-table', organizationId] });
                  onCloseInviteModal?.();
                  setInviteTab('individual');
                }}
                onCancel={() => {
                  onCloseInviteModal?.();
                  setInviteTab('individual');
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Modal: Confirmar Remoção */}
      {confirmRemoveUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !isUpdating && setConfirmRemoveUser(null)}>
          <div className="bg-[var(--bg-surface)] rounded border enterprise-border shadow-xl w-96 p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-1 text-red-500">Remover Usuário</h3>
            <p className="text-xs enterprise-text-secondary mb-4">
              Tem certeza que deseja remover <strong>{confirmRemoveUser.name || confirmRemoveUser.email}</strong> desta organização? Esta ação pode ser revertida re-convidando o usuário.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="enterprise-button-secondary px-3 py-1.5 text-xs" onClick={() => setConfirmRemoveUser(null)} disabled={isUpdating}>
                Cancelar
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                onClick={handleRemoveUser}
                disabled={isUpdating}
              >
                {isUpdating ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </RequireCapability>
  );
};
